import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import {
    Add,
    ArrowDownward,
    ArrowUpward,
    DeleteOutline,
    Edit,
    FormatColorFill,
    GroupWork,
    PhotoLibrary,
    ShortText,
    Tune
} from "@mui/icons-material";
import { TokenField, CAMPAIGN_TOKEN_FIELDS, hasDisplayOptions, labelForToken, parseToken, resolvePath } from "../htmlTokenSchema";
import {
    templateToHtml,
    htmlToTemplate,
    defaultInner,
    generateTokenHtml,
    placeCaretAfterToken,
    caretRangeFromPoint,
    TOKEN_SELECTED_STYLE
} from "./tokenPills";
import { TokenContext, render } from "./tokenRenderer";
import TokenConfigDialog from "../TokenConfigDialog";
import TokenPickerDialog from "./TokenPickerDialog";
import SectionPropertiesDialog from "./SectionPropertiesDialog";
import { FileInput, FileInputType, FileInputFile } from "../file-input";
import { BLOCK_TYPES, BlockType } from "./blockTypes";
import { AddControl, TemplateGroupSummary } from "./ContractAddMenu";

// Shape of a block/section as held in form values (mirrors the generated
// ContractBlock/ContractSection GraphQL types, with nullable fields).
export type ContractBlockValue = {
    id?: string | null,
    type?: string | null,        // 'text' | 'field' | 'image'
    title?: string | null,       // field-block name / image-block caption
    content?: string | null,     // raw text with {{...}} token syntax (text + field blocks)
    sort?: number | null,
    creation_id?: string | null,
    files?: FileInputFile[] | null,
}

export type ContractSectionValue = {
    id?: string | null,
    title?: string | null,
    show_title?: boolean | null,
    new_page?: boolean | null,
    sort?: number | null,
    creation_id?: string | null,
    blocks?: ContractBlockValue[] | null,
}

export type ContractDocumentValue = {
    sections?: ContractSectionValue[] | null,
}

// The shared contract document: renders the section/block tree as the styled
// document (mirroring the server render) and is the in-place editing surface
// in both the campaign Contract tab and the template editor. Edit mode shows
// editing affordances; live mode renders clean and read-only, as the document
// will be produced.
type ContractDocumentProps<T extends TemplateGroupSummary = TemplateGroupSummary> = {
    // The value tree rendered/edited by this component: { sections: [...] }.
    value: ContractDocumentValue | null | undefined,
    // Called with the full updated value tree on every edit (controlled).
    onChange: (value: ContractDocumentValue) => void,
    // The view mode: edit shows affordances, live renders clean and read-only.
    mode: 'edit' | 'live',
    // Permission: a read-only user gets no affordances in either mode.
    readOnly?: boolean,
    // Token value source: the campaign context (resolved values) or the
    // placeholder context (template surface, no campaign data).
    tokenContext: TokenContext,
    // Token fields available to the token picker.
    tokenFields?: TokenField[],
    // Document furniture (header logo, footer). Defaults mirror the server
    // render's fallbacks.
    logoUrl?: string | null,
    footerText?: string | null,
    // Predefined template groups offered by the inline add controls.
    templates?: T[],
    templatesLoading?: boolean,
    onApplyTemplate?: (template: T) => void,
    // The campaign's artwork files, offered by the image block's
    // "pick from artwork" shortcut (inserts an artwork token, no file copy).
    artwork?: { url?: string | null, original_name?: string | null }[] | null,
}

// Temp id for objects not yet persisted; the save pipeline converts it to
// creation_id (same convention as FieldTable addRow / FileInput uploads).
const tempId = (): string => Math.floor(Date.now()) + '-' + Math.floor(Math.random() * 1000000);

// renumbers sort 0..n-1 in place on a copied array
const renumber = <T extends { sort?: number | null }>(items: T[]): T[] =>
    items.map((item, i) => ({ ...item, sort: i }));

// The T&C block is the one carrying the {{ terms }} token; it renders as
// fine print (two-column serif) in both the print view and the editor.
const isFinePrint = (content: string | null | undefined): boolean =>
    (content ?? '').includes('{{ terms }}');

// A borderless standard input that looks like the document's own text until
// hovered or focused (the edit-mode counterpart of the live-mode text).
const inlineInputSx = {
    '& .MuiInput-underline::before': { borderBottom: 'none' },
    '& .MuiInput-underline::after': { borderBottom: 'none' },
    '& .MuiInput-underline:hover::before': { borderBottom: '1px solid #ccc' },
    '& .MuiInput-underline.Mui-focused::before': { borderBottom: '1px solid #999' },
};

// Escapes a string for use inside a CSS content: "" rule (mirrors the blade's
// footerCss escaping).
const cssEscape = (text: string): string =>
    text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r\n/g, '\\A\\A').replace(/\r/g, '\\A').replace(/\n/g, '\\A');

// The @page rule mirrors the blade's print footer.
const documentCss = (footerText: string): string => `
@page {
    size: A4;
    margin: 20mm 16mm 22mm 16mm;
    @bottom-center { font-size: 8pt; color: #555; text-align: center; content: "${cssEscape(footerText)}"; }
}
.cs-document { font-family: 'Roboto', 'Helvetica', Arial, sans-serif; color: #1a1a2e; background: #fff; }
.cs-document .cs-contract { max-width: 190mm; margin: 0 auto; padding: 10mm; }
.cs-document .doc-header { text-align: center; margin-bottom: 28px; }
.cs-document .doc-header img { max-height: 48px; max-width: 220px; }
.cs-document h1 { font-size: 22pt; font-weight: 500; text-align: center; margin: 8px 0 24px; }
.cs-document h2 { font-size: 16pt; font-weight: 600; margin: 28px 0 10px; border-top: 1px solid #ccc; padding-top: 12px; }
.cs-document h3 { font-size: 12pt; font-weight: 600; margin: 20px 0 8px; }
.cs-document h4, .cs-document h5, .cs-document h6 { font-size: 11pt; font-weight: 600; margin: 16px 0 6px; }
.cs-document p { line-height: 1.5; margin: 0 0 10px; }
.cs-document ul, .cs-document ol { line-height: 1.5; margin: 0 0 10px; }
.cs-document .new-page { page-break-before: always; }
.cs-document .block { margin: 0 0 16px; }
.cs-document .block .block-title { font-size: 11pt; font-weight: 600; margin: 0 0 4px; }
.cs-document .field-block { display: grid; grid-template-columns: minmax(140px, 1fr) 2fr; gap: 6px 16px; align-items: baseline; margin: 8px 0; }
.cs-document .field-block .field-name { font-weight: 600; }
.cs-document .image-block { margin: 16px 0; }
.cs-document .image-block img { max-width: 100%; }
.cs-document .contract-artwork { max-width: 100%; }
.cs-document .image-block .caption { font-style: italic; color: #555; margin-top: 4px; font-size: 10pt; }
.cs-document table.contract-table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 10pt; }
.cs-document table.contract-table th, .cs-document table.contract-table td { border: 1px solid #bbb; padding: 6px 10px; text-align: left; vertical-align: top; }
.cs-document table.contract-table thead { display: table-header-group; }
.cs-document table.contract-table th { background: #f2f2f5; font-weight: 600; }
.cs-document .signature { margin-top: 56px; page-break-inside: avoid; }
.cs-document .signature .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 32px; }
.cs-document .signature .sig-line { border-top: 1px solid #333; padding-top: 6px; font-size: 9pt; color: #555; min-height: 48px; }
.cs-document .signature .sig-label { font-weight: 600; color: #333; }
.cs-document .unresolved-token { background: #fff3cd; border-bottom: 1px dotted #b08900; padding: 0 2px; white-space: nowrap; }
.cs-document mark { background: #fff3b0; color: inherit; }
.cs-document table.contract-table thead th { background: #e8e8ee; font-weight: 700; }
.cs-document table.contract-table tbody tr:nth-child(even) { background: #f7f7fa; }
.cs-document table.contract-table tr { page-break-inside: avoid; }
.cs-document .field-block, .cs-document .image-block { page-break-inside: avoid; }
/* Fine-print (Terms & Conditions): mirrors the print view — two columns,
   smaller serif type, so the editor's live view matches the produced PDF. */
.cs-document .fine-print { columns: 2; column-gap: 6mm; font-size: 8.5pt; line-height: 1.4; font-family: Georgia, 'Times New Roman', Times, serif; }
.cs-document .fine-print ol, .cs-document .fine-print ul { list-style: none; padding-left: 1em; }
.cs-document .fine-print p, .cs-document .fine-print h3, .cs-document .fine-print h4, .cs-document .fine-print h5, .cs-document .fine-print h6, .cs-document .fine-print li { break-inside: avoid; }
.cs-document .fine-print h3 { font-size: 10pt; margin: 8px 0 4px; }
.cs-document .fine-print h4, .cs-document .fine-print h5 { font-size: 9.5pt; margin: 8px 0 4px; }
.cs-document .fine-print h6 { font-size: 9pt; margin: 6px 0 3px; }
.cs-document .fine-print p:has(> b:first-child) { padding-left: 2em; text-indent: -2em; }
/* Edit mode only: structural controls are hidden until the user hovers or
   focuses the section/block. Opacity (not display) so the layout never
   shifts; pointer-events so hidden controls are not clickable. */
.cs-document .section-tools, .cs-document .block-header { opacity: 0; pointer-events: none; transition: opacity 0.15s; }
.cs-document .cs-section-edit:hover .section-tools, .cs-document .cs-section-edit:focus-within .section-tools,
.cs-document .cs-block-edit:hover .block-header, .cs-document .cs-block-edit:focus-within .block-header { opacity: 1; pointer-events: auto; }
/* On hover/focus the block itself is NOT tinted; instead the editable
   elements (text, field name + value, image caption) are tinted, so it is
   clear where the user should click. */
.cs-document .cs-block-edit { position: relative; }
.cs-document .cs-block-edit .editable-text, .cs-document .cs-block-edit input { transition: background-color 0.15s; }
.cs-document .cs-block-edit:hover .editable-text, .cs-document .cs-block-edit:focus-within .editable-text,
.cs-document .cs-block-edit:hover input, .cs-document .cs-block-edit:focus-within input { background-color: #e3f2fd; }
/* Left rail: the section's line and number, each block's start/end ticks.
   Positioned in the document's own padding, so no JS measurement is needed
   and the marks track the content as it reflows. */
.cs-document .cs-section-edit { position: relative; border-top: 1px solid #ccc; padding-top: 12px; margin-top: 28px; }
.cs-document .rail-line { position: absolute; left: -24px; top: 0; bottom: 0; width: 2px; background: #ddd; }
.cs-document .rail-num { position: absolute; left: -42px; top: 0; width: 18px; text-align: center; font-size: 10pt; color: #999; }
.cs-document .rail-tick { position: absolute; left: -24px; width: 10px; height: 2px; background: #bbb; }
.cs-document .rail-tick.top { top: 0; }
.cs-document .rail-tick.bottom { bottom: 0; }
/* Field value: styled like the field name input — a text cursor, a bit of
   height, and a faint hover / stronger focus underline (no focus ring). */
.cs-document .field-block .editable-text { border-bottom: 1px solid transparent; cursor: text; font-size: 13.7143px; min-height: 18px; padding: 4px 0; }
.cs-document .field-block .editable-text:hover { border-bottom-color: #ccc; }
.cs-document .field-block .editable-text:focus { border-bottom-color: #999; outline: none; }
.cs-document .field-block .editable-text:empty::before,
.cs-document .field-block .editable-text:has(> br:only-child)::before { content: attr(data-placeholder); color: #aaa; }
/* Text blocks: the browser's thick black focus ring, replaced with a subtle
   one in the document's interactive palette, so the caret stays visible. */
.cs-document .editable-text:focus { outline: 2px solid #90caf9; outline-offset: -2px; }
`;

// A contenteditable text block with token pills. The text is uncontrolled:
// initialized once from the raw value (tokens inflated to pills), synced back
// to the form value on input/blur, and rebuilt from state only when the value
// changes externally and the element is not focused (to avoid cursor jumps).
const EditableText = ({ content, fields, placeholder, onCommit, registerCaret, registerCommit, registerEl, onClick, onMouseDown, onContextMenu, onKeyDown }: {
    content: string,
    fields: TokenField[],
    placeholder?: string,
    onCommit: (next: string) => void,
    registerCaret?: (range: Range) => void,
    registerCommit?: (fn: () => void) => void,
    registerEl?: (el: HTMLDivElement | null) => void,
    onClick?: (e: React.MouseEvent) => void,
    onMouseDown?: (e: React.MouseEvent) => void,
    onContextMenu?: (e: React.MouseEvent) => void,
    onKeyDown?: (e: React.KeyboardEvent) => void,
}): React.ReactElement => {
    const ref = useRef<HTMLDivElement | null>(null);
    const lastValue = useRef(content);

    const setRef = useCallback((el: HTMLDivElement | null) => {
        ref.current = el;
        registerEl?.(el);
        if (el && !el.dataset.initialized) {
            el.innerHTML = templateToHtml(lastValue.current, fields);
            el.dataset.initialized = '1';
        }
    }, [fields, registerEl]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (content !== lastValue.current && document.activeElement !== el) {
            el.innerHTML = templateToHtml(content, fields);
            lastValue.current = content;
        }
    }, [content, fields]);

    const commit = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const next = htmlToTemplate(el.innerHTML);
        if (next !== lastValue.current) {
            lastValue.current = next;
            onCommit(next);
        }
    }, [onCommit]);

    // Snapshots the current collapsed caret so token insertion can restore it
    // even after focus moved to the picker.
    const captureCaret = useCallback(() => {
        const el = ref.current;
        const sel = window.getSelection();
        if (!el || !sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (!el.contains(range.startContainer)) return;
        if (range.collapsed) registerCaret?.(range.cloneRange());
    }, [registerCaret]);

    useEffect(() => {
        registerCommit?.(commit);
    }, [registerCommit, commit]);

    return (
        <div
            ref={setRef}
            contentEditable
            suppressContentEditableWarning
            className="editable-text"
            data-testid="editable-text"
            data-placeholder={placeholder}
            onInput={commit}
            onBlur={commit}
            onSelect={captureCaret}
            onKeyUp={captureCaret}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onContextMenu={onContextMenu}
            onKeyDown={onKeyDown}
        />
    );
};

// The edit/live view toggle for the document. Shared by the campaign Contract
// tab toolbar and the template editor.
export const EditLiveToggle = ({ mode, onChange }: {
    mode: 'edit' | 'live',
    onChange: (mode: 'edit' | 'live') => void,
}): React.ReactElement => (
    <ToggleButtonGroup size="small" value={mode} exclusive onChange={(_, m) => m && onChange(m)}>
        <ToggleButton value="edit">Edit</ToggleButton>
        <ToggleButton value="live">Live</ToggleButton>
    </ToggleButtonGroup>
);

function ContractDocument<T extends TemplateGroupSummary = TemplateGroupSummary>(
    props: ContractDocumentProps<T>
): React.ReactElement {
    const {
        value,
        onChange,
        mode,
        readOnly = false,
        tokenContext,
        tokenFields = CAMPAIGN_TOKEN_FIELDS,
        logoUrl,
        footerText,
        templates,
        templatesLoading,
        onApplyTemplate,
        artwork = null,
    } = props;
    const sections: ContractSectionValue[] = (value?.sections || []).map(s => ({ blocks: [], ...s }));
    // Editing affordances are shown only in edit mode for users with edit
    // permission; live mode and read-only users get the clean render.
    const canEdit = mode === 'edit' && !readOnly;

    const [blockMenu, setBlockMenu] = useState<{ anchor: HTMLElement | null, section: number } | null>(null);
    const [addAfterMenu, setAddAfterMenu] = useState<{ anchor: HTMLElement | null, section: number, block: number } | null>(null);
    const [artworkMenu, setArtworkMenu] = useState<{ anchor: HTMLElement | null, section: number, block: number } | null>(null);
    const [picker, setPicker] = useState<{ section: number, block: number } | null>(null);
    const [sectionProps, setSectionProps] = useState<number | null>(null);
    const [config, setConfig] = useState<{ el: HTMLElement, field: TokenField, inner: string, section: number, block: number } | null>(null);
    const [selectedPill, setSelectedPill] = useState<{ el: HTMLElement, section: number, block: number } | null>(null);
    const [pillMenu, setPillMenu] = useState<{ el: HTMLElement, section: number, block: number } | null>(null);
    const pendingCaret = useRef<{ el: HTMLElement, section: number, block: number } | null>(null);
    // The token pill currently "selected" (chip-style): highlighted, and the
    // target for Delete, Alt+Arrow moves and drag-to-move. The ref mirrors
    // the state so the stable drag handlers can reach it.
    const selectedPillRef = useRef<{ el: HTMLElement, section: number, block: number } | null>(null);
    // Live drag of a pill: the pill, its floating ghost, and the original
    // mousedown, from which the drag threshold is measured. `active` flips on
    // once the threshold is exceeded.
    const dragRef = useRef<{
        el: HTMLElement;
        ghost: HTMLElement | null;
        startX: number;
        startY: number;
        section: number;
        block: number;
        active: boolean;
    } | null>(null);

    const commitFns = useRef(new Map<string, () => void>());
    const carets = useRef(new Map<string, Range>());
    const blockEls = useRef(new Map<string, HTMLDivElement>());

    const blockKey = (si: number, bi: number) => `${si}:${bi}`;

    const emit = useCallback((next: ContractSectionValue[]) => {
        onChange({ sections: next });
    }, [onChange]);

    const setSection = useCallback((si: number, patch: Partial<ContractSectionValue>) => {
        const next = sections.map((s, i) => i === si ? { ...s, ...patch } : s);
        emit(next);
    }, [sections, emit]);

    const setBlock = useCallback((si: number, bi: number, patch: Partial<ContractBlockValue>) => {
        const section = sections[si];
        const blocks = (section.blocks || []).map((b, i) => i === bi ? { ...b, ...patch } : b);
        emit(sections.map((s, i) => i === si ? { ...s, blocks } : s));
    }, [sections, emit]);

    const removeSection = useCallback((si: number) => {
        emit(renumber(sections.filter((_, i) => i !== si)));
    }, [sections, emit]);

    const moveSection = useCallback((si: number, dir: -1 | 1) => {
        const target = si + dir;
        if (target < 0 || target >= sections.length) return;
        const next = [...sections];
        [next[si], next[target]] = [next[target], next[si]];
        emit(renumber(next));
    }, [sections, emit]);

    const addSection = useCallback(() => {
        emit([...sections, { id: tempId(), title: null, show_title: false, new_page: false, sort: sections.length, blocks: [] }]);
    }, [sections, emit]);

    // A blank block chosen while no sections exist starts a new section
    // holding the block (same shape as the template-clone sections).
    const addPlainBlock = useCallback((type: BlockType) => {
        const block: ContractBlockValue = { id: tempId(), type, title: null, content: null, sort: 0, files: [] };
        emit([...sections, { id: tempId(), title: null, show_title: false, new_page: false, sort: sections.length, blocks: [block] }]);
    }, [sections, emit]);

    const addBlock = useCallback((si: number, type: BlockType) => {
        const section = sections[si];
        const blocks = [...(section.blocks || []), {
            id: tempId(),
            type,
            title: null,
            content: null,
            sort: (section.blocks || []).length,
            files: [],
        }];
        emit(sections.map((s, i) => i === si ? { ...s, blocks: renumber(blocks) } : s));
    }, [sections, emit]);

    const removeBlock = useCallback((si: number, bi: number) => {
        const section = sections[si];
        const blocks = (section.blocks || []).filter((_, i) => i !== bi);
        emit(sections.map((s, i) => i === si ? { ...s, blocks: renumber(blocks) } : s));
    }, [sections, emit]);

    const moveBlock = useCallback((si: number, bi: number, dir: -1 | 1) => {
        const section = sections[si];
        const blocks = [...(section.blocks || [])];
        const target = bi + dir;
        if (target < 0 || target >= blocks.length) return;
        [blocks[bi], blocks[target]] = [blocks[target], blocks[bi]];
        emit(sections.map((s, i) => i === si ? { ...s, blocks: renumber(blocks) } : s));
    }, [sections, emit]);

    // Toggles a highlight (<mark>) on the block's current text selection:
    // wraps a fresh selection in <mark>, or unwraps one that is already
    // highlighted. Called from the block header's Highlight button, whose
    // onMouseDown preventDefaults so the selection survives the click.
    const toggleHighlight = useCallback((si: number, bi: number) => {
        const key = blockKey(si, bi);
        const el = blockEls.current.get(key);
        const sel = window.getSelection();
        if (!el || !sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (range.collapsed) return;
        if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;

        const ancestor = range.commonAncestorContainer;
        const elAncestor = ancestor.nodeType === Node.ELEMENT_NODE ? (ancestor as Element) : ancestor.parentElement;
        const existingMark = elAncestor?.closest('mark');
        if (existingMark && el.contains(existingMark)) {
            // Already highlighted: unwrap the <mark> (remove the highlight).
            const frag = document.createDocumentFragment();
            while (existingMark.firstChild) frag.appendChild(existingMark.firstChild);
            existingMark.parentNode?.replaceChild(frag, existingMark);
        } else {
            // Wrap the selection in a <mark>. surroundContents throws when the
            // range crosses element boundaries; extract and wrap instead.
            const mark = document.createElement('mark');
            try {
                range.surroundContents(mark);
            } catch {
                const content = range.extractContents();
                mark.appendChild(content);
                range.insertNode(mark);
            }
        }
        commitFns.current.get(key)?.();
    }, []);

    // Registers the block's editable element for token insertion.
    const registerEl = useCallback((si: number, bi: number, el: HTMLDivElement | null) => {
        const key = blockKey(si, bi);
        if (el) {
            blockEls.current.set(key, el);
        } else {
            blockEls.current.delete(key);
        }
    }, []);

    // Inserts a token pill into the block's editable text at the caret (the
    // live selection, the last known caret, or the end of the text), then
    // opens the config dialog to refine tokens with display options.
    const insertToken = useCallback((si: number, bi: number, field: TokenField) => {
        const key = blockKey(si, bi);
        const el = blockEls.current.get(key);
        if (!el) return;
        el.focus();
        let range = carets.current.get(key) ?? null;
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).startContainer)) {
            const live = sel.getRangeAt(0);
            if (live.collapsed) range = live.cloneRange();
        }
        if (!range || !el.contains(range.startContainer)) {
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
        }
        const inner = defaultInner(field);
        const fragment = document.createElement('div');
        fragment.innerHTML = generateTokenHtml(inner, tokenFields, true);
        const pill = fragment.firstElementChild as HTMLElement;
        range.insertNode(pill);
        if (hasDisplayOptions(field)) {
            setConfig({ el: pill, field, inner, section: si, block: bi });
        } else {
            // No config dialog: the picker dialog's focus trap is still active
            // in the close handler, so restore the caret once it has
            // unmounted (the layout effect below).
            pendingCaret.current = { el: pill, section: si, block: bi };
        }
    }, [tokenFields]);

    // Restores focus and the caret after the target pill once the config
    // dialog (or the picker dialog) has unmounted (the dialog's focus trap
    // is still active in the close handler and would steal focus back).
    useLayoutEffect(() => {
        if (config || picker) return;
        const pending = pendingCaret.current;
        pendingCaret.current = null;
        if (pending && pending.el.isConnected) {
            placeCaretAfterToken(pending.el);
            commitFns.current.get(blockKey(pending.section, pending.block))?.();
        }
    }, [config, picker]);

    // Chip-style selection of a token pill: highlights it and parks the caret
    // directly after it so typing continues where the token sits.
    const selectPill = useCallback((el: HTMLElement, si: number, bi: number) => {
        const prev = selectedPillRef.current;
        if (prev && prev.el !== el) prev.el.classList.remove('token-selected');
        selectedPillRef.current = { el, section: si, block: bi };
        el.classList.add('token-selected');
        setSelectedPill({ el, section: si, block: bi });
        blockEls.current.get(blockKey(si, bi))?.focus();
        placeCaretAfterToken(el);
    }, []);

    // Clears the chip selection.
    const clearPillSelection = useCallback(() => {
        selectedPillRef.current?.el.classList.remove('token-selected');
        selectedPillRef.current = null;
        setSelectedPill(null);
    }, []);

    // Opens the config dialog for an existing pill (field resolved from its
    // data-token).
    const openPillConfig = (el: HTMLElement, si: number, bi: number) => {
        const inner = el.getAttribute('data-token') || '';
        const field = resolvePath(tokenFields, parseToken(inner).path)?.field;
        if (!field) return;
        clearPillSelection();
        setConfig({ el, field, inner, section: si, block: bi });
    };

    // Removes a pill from its block and commits the block.
    const deletePill = (el: HTMLElement, si: number, bi: number) => {
        if (el.isConnected) el.remove();
        if (selectedPillRef.current?.el === el) clearPillSelection();
        commitFns.current.get(blockKey(si, bi))?.();
    };

    // Moves a pill one sibling unit left/right within its block (a text run
    // counts as one unit; empty text nodes are skipped), then commits the
    // block.
    const movePill = (el: HTMLElement, si: number, bi: number, dir: -1 | 1) => {
        let node: Node | null = dir === -1 ? el.previousSibling : el.nextSibling;
        while (node && node.nodeType === Node.TEXT_NODE && !(node as Text).textContent) {
            node = dir === -1 ? node.previousSibling : node.nextSibling;
        }
        if (!node || !node.parentNode) return;
        node.parentNode.insertBefore(el, dir === -1 ? node : node.nextSibling);
        commitFns.current.get(blockKey(si, bi))?.();
    };

    // One click on a pill selects it (chip-style); a second click on the
    // already-selected pill opens its config dialog. Clicking anywhere that
    // is not a pill clears the selection.
    const handlePillClick = (si: number, bi: number) => (e: React.MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.atomic-token') as HTMLElement | null;
        if (target) {
            if (selectedPillRef.current?.el === target) {
                openPillConfig(target, si, bi);
            } else {
                selectPill(target, si, bi);
            }
        } else if (selectedPillRef.current) {
            clearPillSelection();
        }
    };

    // Right-click on a pill: the Edit / Delete context menu.
    const handlePillContextMenu = (si: number, bi: number) => (e: React.MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.atomic-token') as HTMLElement | null;
        if (!target) return;
        e.preventDefault();
        setPillMenu({ el: target, section: si, block: bi });
    };

    // While a pill is selected it absorbs the editing keys that would
    // otherwise act on the caret: delete and Alt+Arrow moves.
    const handlePillKeyDown = (si: number, bi: number) => (e: React.KeyboardEvent) => {
        const selected = selectedPillRef.current;
        if (!selected || selected.section !== si || selected.block !== bi) return;
        if (e.key === 'Escape') {
            e.preventDefault();
            clearPillSelection();
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            deletePill(selected.el, si, bi);
        } else if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                movePill(selected.el, si, bi, e.key === 'ArrowLeft' ? -1 : 1);
            }
        }
    };

    // Starts a potential pill drag on mousedown. The ghost is only created
    // once the pointer has moved past a small threshold, so plain clicks
    // (select / edit) behave exactly as before and nothing flashes on screen.
    const handlePillMouseDown = (si: number, bi: number) => (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const target = (e.target as HTMLElement).closest('.atomic-token') as HTMLElement | null;
        if (!target) return;
        e.preventDefault();
        dragRef.current = { el: target, ghost: null, startX: e.clientX, startY: e.clientY, section: si, block: bi, active: false };
        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd, { once: true });
    };

    const handleDragMove = useCallback((e: MouseEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        if (!drag.active) {
            if (Math.abs(e.clientX - drag.startX) + Math.abs(e.clientY - drag.startY) < 5) return;
            drag.active = true;
            const ghost = drag.el.cloneNode(true) as HTMLElement;
            ghost.classList.remove('token-selected');
            // The clone keeps the pill's inline style (background, border,
            // nowrap); only the positioning properties are overridden, so the
            // ghost looks like the pill instead of raw text.
            ghost.style.position = 'fixed';
            ghost.style.zIndex = '2000';
            ghost.style.pointerEvents = 'none';
            ghost.style.opacity = '0.7';
            ghost.style.width = `${drag.el.offsetWidth}px`;
            document.body.appendChild(ghost);
            drag.ghost = ghost;
        }
        if (drag.ghost) {
            drag.ghost.style.left = `${e.clientX}px`;
            drag.ghost.style.top = `${e.clientY}px`;
        }
    }, []);

    const handleDragEnd = useCallback((e: MouseEvent) => {
        window.removeEventListener('mousemove', handleDragMove);
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag) return;
        drag.ghost?.remove();
        if (!drag.active) return; // was a plain click; the onClick handler decides
        if (!drag.el.isConnected) return;
        const range = caretRangeFromPoint(e.clientX, e.clientY);
        // A point over any pill (the dragged one or another) resolves into
        // the token's interior; dropping there would nest pills, so those
        // points are invalid.
        const containerNode = range?.startContainer;
        const containerEl = containerNode && containerNode.nodeType === Node.TEXT_NODE
            ? containerNode.parentElement
            : (containerNode as Element | null | undefined) ?? null;
        const insideToken = !!containerEl?.closest('.atomic-token');
        // The drop target is the block's editable element containing the
        // caret (the same block, or another one for a cross-block move).
        let targetKey: string | null = null;
        if (range && !insideToken) {
            for (const [key, el] of blockEls.current) {
                if (el.contains(range.startContainer)) {
                    targetKey = key;
                    break;
                }
            }
        }
        if (!range || !targetKey) return; // no valid drop point; the pill stays put
        const [tsi, tbi] = targetKey.split(':').map(Number);
        range.collapse(true);
        const clone = drag.el.cloneNode(true) as HTMLElement;
        clone.classList.remove('token-selected');
        range.insertNode(clone);
        drag.el.remove();
        // Commit both the source and target blocks (a no-op when they are
        // the same block).
        commitFns.current.get(blockKey(drag.section, drag.block))?.();
        commitFns.current.get(targetKey)?.();
        selectPill(clone, tsi, tbi);
    }, [selectPill, handleDragMove]);

    // Cancels an in-flight pill drag when the component unmounts.
    useEffect(() => () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
    }, [handleDragMove, handleDragEnd]);

    // The selected pill is DOM-level state; when a block's editable element
    // is rebuilt (an external value change, a block removed) the node no
    // longer exists, so the selection is dropped.
    useEffect(() => {
        if (selectedPill && !selectedPill.el.isConnected) clearPillSelection();
    }, [selectedPill, sections, clearPillSelection]);

    // Live (or read-only) block render: the resolved document content.
    // Raw block content is NOT clamped (the server blade renders it as-is);
    // only the terms token value is clamped, inside the context (mirroring
    // the server's valueFor), so no token value is clamped twice.
    const renderContent = (content: string | null): string =>
        render(content ?? '', tokenContext);

    const staticBlock = (si: number, bi: number, block: ContractBlockValue) => {
        switch (block.type) {
            case 'field':
                return (
                    <div className="block field-block" key={block.id ?? bi}>
                        <div className="field-name">{block.title}</div>
                        <div className="field-value">{renderContent(block.content)}</div>
                    </div>
                );
            case 'image': {
                const file = (block.files ?? []).find(f => f.url);
                return (
                    <div className="block image-block" key={block.id ?? bi}>
                        {file && <img src={file.url} alt={block.title || ''} />}
                        {block.title && <div className="caption">{block.title}</div>}
                    </div>
                );
            }
            case 'text':
            default:
                return (
                    <div className={"block" + (isFinePrint(block.content) ? " fine-print" : "")} key={block.id ?? bi}>
                        {block.title && <div className="block-title">{block.title}</div>}
                        <div dangerouslySetInnerHTML={{ __html: renderContent(block.content) }} />
                    </div>
                );
        }
    };

    // Edit-mode block: the in-place editor (contenteditable text with token
    // pills, field name + value, image upload + caption).
    const editableBlock = (si: number, bi: number, block: ContractBlockValue) => {
        const key = blockKey(si, bi);
        const editable = (placeholder?: string) => (
            <EditableText
                content={block.content ?? ''}
                fields={tokenFields}
                placeholder={placeholder}
                onCommit={(next) => setBlock(si, bi, { content: next })}
                registerCaret={(range) => carets.current.set(key, range)}
                registerCommit={(fn) => commitFns.current.set(key, fn)}
                registerEl={(el) => registerEl(si, bi, el)}
                onClick={handlePillClick(si, bi)}
                onMouseDown={handlePillMouseDown(si, bi)}
                onContextMenu={handlePillContextMenu(si, bi)}
                onKeyDown={handlePillKeyDown(si, bi)}
            />
        );
        switch (block.type) {
            case 'field':
                return (
                    <div className="block field-block" key={block.id ?? bi}>
                        <TextField
                            variant="standard"
                            size="small"
                            placeholder="Field name"
                            value={block.title ?? ''}
                            onChange={(evt) => setBlock(si, bi, { title: evt.target.value })}
                            sx={{ fontWeight: 600, ...inlineInputSx }}
                        />
                        {editable('Field value')}
                    </div>
                );
            case 'image':
                return (
                    <div className="block image-block" key={block.id ?? bi}>
                        {(block.files ?? []).map(f => f.url && <img key={f.id} src={f.thumbnail_url || f.url} alt={block.title || ''} style={{ maxWidth: 400 }} />)}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PhotoLibrary fontSize="small" />}
                                disabled={!artwork || artwork.filter(f => f.original_name).length === 0}
                                onClick={(evt) => setArtworkMenu({ anchor: evt.currentTarget, section: si, block: bi })}
                                title="Insert a token referencing one of the campaign's artwork files"
                            >
                                Pick from artwork
                            </Button>
                        </Box>
                        <FileInput
                            name={`contract-doc-${si}-${bi}-files`}
                            type={FileInputType.Image}
                            showPreview={false}
                            multiple={false}
                            maxImageSize={900}
                            label="Upload image"
                            fileType="CT"
                            value={block.files ?? []}
                            allowDelete={true}
                            readOnly={false}
                            onChange={(evt) => {
                                const v = evt.target.value;
                                const files: FileInputFile[] = typeof v === 'function' ? v(block.files ?? []) : (v ?? []);
                                setBlock(si, bi, { files });
                            }}
                        />
                        <TextField
                            variant="standard"
                            size="small"
                            placeholder="Caption"
                            value={block.title ?? ''}
                            onChange={(evt) => setBlock(si, bi, { title: evt.target.value })}
                            sx={{ fontStyle: 'italic', color: '#555', fontSize: '10pt', ...inlineInputSx }}
                        />
                    </div>
                );
            case 'text':
            default:
                return (
                    <div className={"block" + (isFinePrint(block.content) ? " fine-print" : "")} key={block.id ?? bi}>
                        {block.title && <div className="block-title">{block.title}</div>}
                        {editable()}
                    </div>
                );
        }
    };

    // The section-level inline add-block menu: the three blank block types
    // plus the predefined template groups (labeled with their section count).
    const sectionAddMenu = (
        <Menu open={!!blockMenu} anchorEl={blockMenu?.anchor} onClose={() => setBlockMenu(null)}>
            {BLOCK_TYPES.map(bt => (
                <ListItem key={bt.type} disableGutters>
                    <ListItemButton onClick={() => {
                        if (blockMenu === null) return;
                        addBlock(blockMenu.section, bt.type);
                        setBlockMenu(null);
                    }}>
                        <ListItemIcon sx={{ minWidth: 34 }}>{bt.icon}</ListItemIcon>
                        <ListItemText>{bt.label}</ListItemText>
                    </ListItemButton>
                </ListItem>
            ))}
            {templates !== undefined && (
                <>
                    <Divider />
                    <Typography variant="subtitle2" color="textSecondary" sx={{ px: 2, pt: 1, pb: 0.5 }}>Predefined groups</Typography>
                    {templatesLoading ? (
                        <List disablePadding>
                            <ListItem disableGutters>
                                <ListItemText><CircularProgress size={20} /></ListItemText>
                            </ListItem>
                        </List>
                    ) : (templates ?? []).length === 0 ? (
                        <List disablePadding>
                            <ListItem disableGutters>
                                <ListItemText primary={<Typography variant="body2" color="textSecondary">No predefined groups available</Typography>} />
                            </ListItem>
                        </List>
                    ) : (
                        <List dense disablePadding>
                            {(templates ?? []).map(t => (
                                <ListItem key={t.id} disableGutters>
                                    <ListItemButton disabled={!onApplyTemplate} onClick={() => { onApplyTemplate?.(t); setBlockMenu(null); }}>
                                        <ListItemIcon sx={{ minWidth: 34 }}><GroupWork fontSize="small" /></ListItemIcon>
                                        <ListItemText primary={t.name || 'Untitled group'} secondary={(t.sections ?? []).length + ' section' + ((t.sections ?? []).length === 1 ? '' : 's')} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            )}
        </Menu>
    );

    // The block-level add-after menu: the three blank block types.
    const addAfterMenuEl = (
        <Menu open={!!addAfterMenu} anchorEl={addAfterMenu?.anchor} onClose={() => setAddAfterMenu(null)}>
            {BLOCK_TYPES.map(bt => (
                <ListItem key={bt.type} disableGutters>
                    <ListItemButton onClick={() => {
                        if (addAfterMenu === null) return;
                        const blocks = [...(sections[addAfterMenu.section].blocks || [])];
                        blocks.splice(addAfterMenu.block + 1, 0, {
                            id: tempId(),
                            type: bt.type,
                            title: null,
                            content: null,
                            sort: addAfterMenu.block + 1,
                            files: [],
                        });
                        emit(sections.map((s, i) => i === addAfterMenu.section ? { ...s, blocks: renumber(blocks) } : s));
                        setAddAfterMenu(null);
                    }}>
                        <ListItemIcon sx={{ minWidth: 34 }}>{bt.icon}</ListItemIcon>
                        <ListItemText>Add {bt.label.toLowerCase()} after</ListItemText>
                    </ListItemButton>
                </ListItem>
            ))}
        </Menu>
    );

    // The image block's "pick from artwork" menu: lists the campaign's artwork
    // files; picking one inserts a text block right after the image block
    // carrying an artwork token (no file copy - the token resolves at render
    // time, so re-uploads keep working).
    const pickArtwork = (name: string) => {
        if (artworkMenu === null) return;
        const blocks = [...(sections[artworkMenu.section].blocks || [])];
        blocks.splice(artworkMenu.block + 1, 0, {
            id: tempId(),
            type: 'text',
            title: null,
            content: `{{ artwork : image : ${name} }}`,
            sort: artworkMenu.block + 1,
            files: [],
        });
        emit(sections.map((s, i) => i === artworkMenu.section ? { ...s, blocks: renumber(blocks) } : s));
        setArtworkMenu(null);
    };

    const artworkMenuEl = (
        <Menu open={!!artworkMenu} anchorEl={artworkMenu?.anchor} onClose={() => setArtworkMenu(null)}>
            {(artwork ?? []).filter(f => f.original_name).length === 0 ? (
                <ListItem disableGutters>
                    <ListItemText primary={<Typography variant="body2" color="textSecondary">No artwork on this campaign</Typography>} />
                </ListItem>
            ) : (
                (artwork ?? []).filter(f => f.original_name).map((f, i) => (
                    <ListItem key={i} disableGutters>
                        <ListItemButton onClick={() => pickArtwork(f.original_name!)}>
                            {f.url && (
                                <ListItemAvatar>
                                    <img src={f.url} alt={f.original_name || ''} style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain' }} />
                                </ListItemAvatar>
                            )}
                            <ListItemText primary={f.original_name} />
                        </ListItemButton>
                    </ListItem>
                ))
            )}
        </Menu>
    );


    const blockHeader = (si: number, bi: number, block: ContractBlockValue) => {
        const blocks = sections[si].blocks || [];
        const bt = BLOCK_TYPES.find(x => x.type === block.type) ?? BLOCK_TYPES[0];
        return (
            <Box className="block-header" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                {bt.icon}
                <Typography variant="body2" color="textSecondary">{bt.label}</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25, alignItems: 'center' }}>
                    <Button size="small" variant="outlined" startIcon={<ShortText fontSize="small" />} onClick={() => setPicker({ section: si, block: bi })}>Insert token</Button>
                    {(block.type === 'text' || block.type === 'field') && (
                        <IconButton size="small" title="Toggle highlight on selection" onMouseDown={(evt) => { evt.preventDefault(); toggleHighlight(si, bi); }}><FormatColorFill fontSize="small" /></IconButton>
                    )}
                    <IconButton size="small" title="Add block after" onClick={(evt) => setAddAfterMenu({ anchor: evt.currentTarget, section: si, block: bi })}><Add fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={bi === 0} onClick={() => moveBlock(si, bi, -1)} title="Move block up"><ArrowUpward fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={bi === blocks.length - 1} onClick={() => moveBlock(si, bi, 1)} title="Move block down"><ArrowDownward fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => removeBlock(si, bi)} title="Delete block"><DeleteOutline fontSize="small" /></IconButton>
                </Box>
            </Box>
        );
    };

    const sectionHeader = (si: number, section: ContractSectionValue) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
                variant="standard"
                size="small"
                sx={{ flex: 1, '& .MuiInput-input': { fontSize: '16pt', fontWeight: 600 }, ...inlineInputSx }}
                placeholder="Section title"
                value={section.title ?? ''}
                onChange={(evt) => setSection(si, { title: evt.target.value })}
            />
            <Box className="section-tools" sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>Section</Typography>
                <IconButton size="small" title="Section properties" onClick={() => setSectionProps(si)}><Tune fontSize="small" /></IconButton>
                <IconButton size="small" disabled={si === 0} onClick={() => moveSection(si, -1)} title="Move section up"><ArrowUpward fontSize="small" /></IconButton>
                <IconButton size="small" disabled={si === sections.length - 1} onClick={() => moveSection(si, 1)} title="Move section down"><ArrowDownward fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => removeSection(si)} title="Delete section"><DeleteOutline fontSize="small" /></IconButton>
            </Box>
        </Box>
    );

    // The static (live / read-only) document: mirrors the server render.
    const staticDocument = (
        <div className="cs-contract">
            <div className="doc-header">
                <img src={logoUrl ?? '/images/logo-BH-2021.png'} alt="Build Hollywood" />
            </div>
            <h1>Creative Studio Agreement for Purchase of Services</h1>
            <div className="doc-body">
                {sections.map((section, si) => (
                    <section key={section.id ?? si}>
                        {section.new_page && si !== 0 && <div className="new-page" />}
                        {section.show_title && <h2>{section.title}</h2>}
                        {(section.blocks || []).map((block, bi) => staticBlock(si, bi, block))}
                    </section>
                ))}
            </div>
            <div className="signature">
                <div className="sig-grid">
                    <div className="sig-line"><div className="sig-label">Name</div></div>
                    <div className="sig-line"><div className="sig-label">Job Title</div></div>
                    <div className="sig-line"><div className="sig-label">Company</div></div>
                    <div className="sig-line"><div className="sig-label">Date</div></div>
                </div>
            </div>
        </div>
    );

    // The edit document: the same rendered document with inline editing
    // affordances (section/block header rows, contenteditable text, add
    // controls).
    const editDocument = (
        <div className="cs-contract is-editing">
            <div className="doc-header">
                <img src={logoUrl ?? '/images/logo-BH-2021.png'} alt="Build Hollywood" />
            </div>
            <h1>Creative Studio Agreement for Purchase of Services</h1>
            <div className="doc-body">
                {sections.map((section, si) => (
                    <section key={section.id ?? si} className="cs-section-edit">
                        <span className="rail-line" />
                        <span className="rail-num">{si + 1}</span>
                        {sectionHeader(si, section)}
                        {(section.blocks || []).map((block, bi) => (
                            <div key={block.id ?? bi} className={block.type === 'field' ? 'cs-block-edit cs-field' : 'cs-block-edit'}>
                                <span className="rail-tick top" />
                                <span className="rail-tick bottom" />
                                {blockHeader(si, bi, block)}
                                {editableBlock(si, bi, block)}
                            </div>
                        ))}
                        <Box className="section-tools" sx={{ mt: 1 }}>
                            <Button
                                size="small"
                                variant="text"
                                startIcon={<Add fontSize="small" />}
                                onClick={(evt) => setBlockMenu({ anchor: evt.currentTarget, section: si })}
                            >
                                Add block
                            </Button>
                        </Box>
                    </section>
                ))}
            </div>
            <div className="signature">
                <div className="sig-grid">
                    <div className="sig-line"><div className="sig-label">Name</div></div>
                    <div className="sig-line"><div className="sig-label">Job Title</div></div>
                    <div className="sig-line"><div className="sig-label">Company</div></div>
                    <div className="sig-line"><div className="sig-label">Date</div></div>
                </div>
            </div>
        </div>
    );

    // The empty-state drop zone: the document-level entry point while the
    // document has no sections.
    const emptyState = (
        <Box
            sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
            }}
        >
            <span>No contract content yet</span>
            {!readOnly && (
                <AddControl
                    hasSections={false}
                    templates={templates}
                    templatesLoading={templatesLoading}
                    onAddSection={addSection}
                    onAddBlockType={addPlainBlock}
                    onApplyTemplate={onApplyTemplate}
                />
            )}
        </Box>
    );

    return (
        <div className="cs-document">
            <style>{documentCss(footerText ?? `© ${new Date().getUTCFullYear()} Build Hollywood Ltd.`)}</style>
            <style>{TOKEN_SELECTED_STYLE}</style>
            {sections.length === 0 ? (
                emptyState
            ) : canEdit ? (
                editDocument
            ) : (
                staticDocument
            )}
            {sectionAddMenu}
            {addAfterMenuEl}
            {artworkMenuEl}
            {picker && (
                <TokenPickerDialog
                    open
                    fields={tokenFields}
                    onPick={(field) => {
                        insertToken(picker.section, picker.block, field);
                        setPicker(null);
                    }}
                    onClose={() => setPicker(null)}
                />
            )}
            {sectionProps !== null && (
                <SectionPropertiesDialog
                    open
                    section={sections[sectionProps]}
                    onChange={(patch) => setSection(sectionProps, patch)}
                    onClose={() => setSectionProps(null)}
                />
            )}
            {pillMenu && (
                <Menu anchorEl={pillMenu.el} open onClose={() => setPillMenu(null)}>
                    <MenuItem onClick={() => { openPillConfig(pillMenu.el, pillMenu.section, pillMenu.block); setPillMenu(null); }}>
                        <Edit fontSize="small" sx={{ mr: 1 }} />
                        Edit
                    </MenuItem>
                    <MenuItem onClick={() => { deletePill(pillMenu.el, pillMenu.section, pillMenu.block); setPillMenu(null); }}>
                        <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
                        Delete
                    </MenuItem>
                </Menu>
            )}
            {config && (
                <TokenConfigDialog
                    open
                    field={config.field}
                    fields={tokenFields}
                    currentInner={config.inner}
                    disableRestoreFocus
                    artwork={artwork}
                    onApply={(newInner) => {
                        config.el.setAttribute('data-token', newInner);
                        config.el.textContent = labelForToken(newInner, tokenFields);
                        config.el.removeAttribute('data-fresh');
                        pendingCaret.current = { el: config.el, section: config.section, block: config.block };
                        setConfig(null);
                    }}
                    onClose={() => {
                        pendingCaret.current = { el: config.el, section: config.section, block: config.block };
                        setConfig(null);
                    }}
                />
            )}
        </div>
    );
}

export default ContractDocument;
